import { __ } from "@wordpress/i18n";

/**
 * Configuration for string replacements
 * An array of objects where keys are variable names (with or without braces) and values are replacement strings
 *
 * @example
 * ```ts
 * const config: StringReplacementConfig = [
 *   { '{{credits}}': '500', '{{credits_currency}}': 'points' }
 * ];
 * replaceStrings('You have {{credits}} {{credits_currency}}', config);
 * // Returns: "You have 500 points"
 * ```
 */
export type StringReplacementConfig = Array<Record<string, string>>;

/**
 * Replace variables in a string with their values
 *
 * Supports variables in the format {{variable}} with optional whitespace.
 * The replacement keys can include or exclude the braces.
 *
 * @param text - The text containing variables to replace
 * @param config - Array of replacement objects
 * @returns The text with variables replaced
 *
 * @example
 * ```ts
 * replaceStrings('Hello {{name}}!', [{ '{{name}}': 'World' }]);
 * // Returns: "Hello World!"
 *
 * replaceStrings('You have {{ credits }} points', [{ 'credits': '500' }]);
 * // Returns: "You have 500 points"
 * ```
 */
export function replaceStrings(text: string, config: StringReplacementConfig): string {
    return config.reduce((result, replacementObj) => {
        Object.entries(replacementObj).forEach(([key, value]) => {
            // Extract the variable name without braces if present
            const variableName = key.replace(/^\{\{\s*|\s*\}\}$/g, '').trim();
            // Create regex to match the variable with optional whitespace
            const regex = new RegExp(`\\{\\{\\s*${variableName}\\s*\\}\\}`, "g");
            result = result.replace(regex, value ?? "");
        });
        return result;
    }, text);
}


